import {
  Outlet, useLocation, Link,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Components
import {
  ButtonMenu,
  Page,
  SearchBar,
} from '@zolteam/zol-admin-ui';
import MessageList from '../cortizol/molecules/MessageList/MessageList';
import Message from '../cortizol/atoms/Message/Message';
// Misc
import * as CortizolMenu from '../../routes/cortizol/menu';
import Logo from '../../assets/client_logo_simple.svg?react';
import { hasPermission } from '../../services/parazol/core';
import useGlobalSearch from '../../hooks/cortizol/useGlobalSearch';
// Context
import { useAppContext } from '../../store/AppContext';
// Services
import { useGetAllContentTypesQuery }
  from '../../services/cortizol/content-type/contentTypeQueries';
import useParazol from '../../services/parazol/useParazol';

/**
 * Represents the menu permissions for the layout component.
 * @typedef {object} MenuPermissions
 * @property {Array<string>} users
 * @property {Array<string>} contentTypes
 * @property {Array<string>} media
 */
/**
 * @type {MenuPermissions}
 */
const MENU_PERMISSIONS = {
  users: [],
  contentTypes: [],
  media: [],
};

/**
 * Creates menu entries.
 * @param {ContentType[]} contentTypesListPayload
 * @param {{contentTypes: Array<string>, media: Array<string>, users: Array<string>}} permissions
 * @param {Array<string>} userPermissions
 * @param {Function} translate
 * @returns {Array<{label:string, entries: Array<CortizolMenu.MenuEntry>}>}
 */
const configureMenuEntries = (
  contentTypesListPayload,
  permissions,
  userPermissions,
  translate,
) => ([
  CortizolMenu.createMenuEntries(
    permissions,
    userPermissions,
    translate,
    contentTypesListPayload,
  ),
  {
    label: translate('cortizol.menu.generalCategoryLabel'),
    entries: [
      {
        label: translate('cortizol.menu.specialCollectionsLabel'),
        href: '/special-collections',
        icon: 'setting',
        isAllowed: true, // TODO adapt permission
      },
      {
        label: translate('cortizol.menu.garageExclusionsLabel'),
        href: '/garage-exclusions',
        icon: 'eye-twotone',
        isAllowed: true, // TODO adapt permission
      },
      {
        label: translate('cortizol.menu.usersLabel'),
        href: '/users',
        icon: 'profile2',
        isAllowed: hasPermission(userPermissions, permissions.users || []),
      },
    ],
  },
].filter((entry) => entry.entries.some((subEntry) => subEntry.isAllowed)));

/**
 *
 * @returns {JSX.Element}
 */
function Layout() {
  // Hooks
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const {
    handleSearchChange,
    areSearchQueriesLoading,
    searchResultsOptions,
    handleSearchResultClick,
  } = useGlobalSearch();
  const [context, dispatch] = useAppContext();
  const { disconnectUser } = useParazol();

  // API calls
  const { data } = useGetAllContentTypesQuery();

  // Handlers
  const handleLogout = () => {
    disconnectUser();
  };
  /**
   * Handles the message closing.
   * @param {number} id
   * @returns {void}
   */
  const handleCloseMessage = (id) => dispatch({ type: 'DELETE_MESSAGE', payload: id });

  return (
    <>
      <Page
        pathname={pathname}
        Link={Link}
        menus={configureMenuEntries(
          data?.data,
          MENU_PERMISSIONS,
          [],
          t,
        )}
        navigationLabel={t('cortizol.menu.foldAction')}
        logo={<Logo />}
        search={(
          <SearchBar
            placeholder={t('cortizol.searchbar.searchPlaceholder')}
            emptySearchResultLabel={t('cortizol.searchbar.emptySearchResultLabel')}
            onChange={handleSearchChange}
            searchOptions={searchResultsOptions}
            onItemClick={handleSearchResultClick}
            isSearchLoading={areSearchQueriesLoading}
          />
      )}
        footer={(
          <ButtonMenu
            icon="logout"
            color="alert"
            onClick={handleLogout}
          >
            {t('cortizol.menu.logout')}
          </ButtonMenu>
      )}
      // @ts-ignore
      // eslint-disable-next-line no-undef
        version={APP_VERSION}
      >
        <Outlet />
      </Page>
      <MessageList>
        {context.messages.map((message) => (
          <Message
            id={message.id}
            text={message.text}
            type={message.type}
            isSelfClosing={message.isSelfClosing}
            onClose={handleCloseMessage}
          />
        ))}
      </MessageList>
    </>
  );
}

export default Layout;
